import { useTranslation } from '@shared/core';
import { BoxProps, Flex } from '@withjoy/joykit';
import React from 'react';
import { StyledContainer, StyledItemContainer, StyledItemIcon, StyledItemTitle, StyledTitle, styles } from './PdpValuePropsSection.styles';
import { PriceMatch } from '../ValuePropsSection/icons';
import { ReactComponent as Discount } from '../../../../../../../../../shared/joykit/packages/core/icons/svgs/32/discount-tag.svg';
import { ReactComponent as FreeShipping } from '../../../../../../../../registry/admin/assets/icon-shipping-truck.svg';
import { SkeletonText } from '@shared/components/Skeleton';
import { useShopStorefrontContext } from '../../../../components/ShopStorefrontProvider';
import { REGISTRY_SHIPPING_THRESHOLD } from '@apps/registry/common/util/constants';

interface ValuePropsSectionItemProps {
  title: string;
  icon: JSX.Element;
  link?: string;
}

const ValuePropsSectionItem: React.FC<ValuePropsSectionItemProps> = ({ title, icon, link }) => {
  return (
    <StyledItemContainer
      __css={styles.itemContainer}
      onClick={() => {
        window.open(link, '_blank');
      }}
      cursor="pointer"
    >
      <StyledItemIcon __css={styles.itemIcon}>{icon}</StyledItemIcon>
      <StyledItemTitle
        typographyVariant="hed1"
        {...styles.itemTitle}
      >
        {title}
      </StyledItemTitle>
    </StyledItemContainer>
  );
};

export const PdpValuePropsSection: React.FC<BoxProps & { showTitle?: boolean }> & { Skeleton: typeof PdpValuePropsSectionSkeleton } = ({ showTitle, ...restProps }) => {
  const { t } = useTranslation('catalogRegistry');
  const translations = t('valuePropsSection');
  const { config } = useShopStorefrontContext();
  const valuePropsLinksByStorefront = config.valuePropSectionLinks;

  return (
    <StyledContainer
      __css={styles.container}
      {...restProps}
    >
      {showTitle && (
        <StyledTitle
          typographyVariant={['hed4', null, 'hed5']}
          __css={styles.title}
        >
          {translations.title()}
        </StyledTitle>
      )}

      <Flex
        justifyContent={'space-between'}
        columnGap={4}
      >
        <ValuePropsSectionItem
          title={translations.postWeddingDiscount()}
          icon={<Discount />}
          link={valuePropsLinksByStorefront.completionDiscount}
        />
        <ValuePropsSectionItem
          title={translations.freeShipping({ price: REGISTRY_SHIPPING_THRESHOLD })}
          icon={<FreeShipping />}
          link={valuePropsLinksByStorefront.shipping}
        />
        <ValuePropsSectionItem
          title={translations.priceMatchPromise()}
          icon={<PriceMatch />}
          link={valuePropsLinksByStorefront.priceMatchPromise}
        />
      </Flex>
    </StyledContainer>
  );
};

const PdpValuePropsSectionSkeleton = (props: BoxProps) => {
  return (
    <StyledContainer __css={styles.container}>
      <Flex justifyContent={'space-between'}>
        <SkeletonText
          rows={1}
          skeletonHeight={'70px'}
          width={'122px'}
        />
        <SkeletonText
          rows={1}
          skeletonHeight={'70px'}
          width={'122px'}
        />
        <SkeletonText
          rows={1}
          skeletonHeight={'70px'}
          width={'122px'}
        />
      </Flex>
    </StyledContainer>
  );
};

PdpValuePropsSection.Skeleton = PdpValuePropsSectionSkeleton;
